import {
  Checkbox,
  PageTitle,
  PageWrapper,
  Section,
  Spinner,
  Table,
} from "@cinesys/ui/components";
import React from "react";
import { useMutation, useQuery } from "react-query";
import { useParams } from "react-router-dom";
import ApiService from "../../services/ApiService";
import styled from "styled-components";

const FlagDescription = styled.div`
  color: var(--gray100);
`;

/**
 * Dashboard-side Feature Flags tab (Monday 12984268291) — moved off
 * ConfigureOrganizationEditScreen.tsx onto its own tab, modeled on UsageAdminScreen.tsx.
 * `GET .../feature-flags` already lists every entry in `FEATURE_FLAG_DEFINITIONS`, so this
 * screen shows every registered flag with no further work; surfacing customer-apps' separate
 * flat rollout flags here is out of scope (Monday 12984409165).
 */
const FeatureFlagsScreen = () => {
  const { systemDomainId } = useParams<{ systemDomainId: string }>();

  const {
    data: featureFlagsData,
    isLoading: featureFlagsLoading,
    isError: featureFlagsError,
    refetch: refetchFeatureFlags,
  } = useQuery({
    queryKey: ["featureFlags", systemDomainId],
    enabled: !!systemDomainId,
    queryFn: async (): Promise<FeatureFlagsResponse> =>
      ApiService.fetch<FeatureFlagsResponse>(
        "GET",
        `/api/config/organization/${systemDomainId}/feature-flags`
      ),
  });

  const setFlagEnabledMutation = useMutation({
    mutationFn: async ({ key, enabled }: { key: string; enabled: boolean }) => {
      await ApiService.fetch(
        "POST",
        `/api/config/organization/${systemDomainId}/feature-flags/set_enabled`,
        { key, enabled }
      );
      await refetchFeatureFlags();
    },
  });

  return (
    <div>
      <PageTitle
        title="Feature Flags"
        error={
          featureFlagsError
            ? "Problem loading feature flags"
            : setFlagEnabledMutation.isError
            ? "Error saving feature flag"
            : undefined
        }
      />
      {featureFlagsLoading ? (
        <Spinner />
      ) : (
        !featureFlagsError && (
          <PageWrapper>
            <Section>
              {!featureFlagsData?.flags.length ? (
                <div style={{ padding: "5px 10px" }}>
                  No flags registered for this platform yet.
                </div>
              ) : (
                <Table
                  style={
                    {
                      "--tableHeaderBg": "transparent",
                      "--tableHeaderBorderBottom": "1px solid var(--dark300)",
                      "--tableHeaderShadow": "none",
                    } as React.CSSProperties
                  }
                  columnGrid
                  columns={[
                    { title: "Flag", width: "1fr" },
                    { title: "", width: "auto" },
                  ]}
                  rows={featureFlagsData.flags.map((flag) => ({
                    id: flag.key,
                    element: () => (
                      <>
                        <div>
                          <div>{flag.key}</div>
                          <FlagDescription>{flag.description}</FlagDescription>
                        </div>
                        <Checkbox
                          label=""
                          inlineLabel
                          value={flag.enabled}
                          onChange={(value) =>
                            setFlagEnabledMutation.mutate({
                              key: flag.key,
                              enabled: value,
                            })
                          }
                          disabled={
                            setFlagEnabledMutation.isLoading &&
                            setFlagEnabledMutation.variables?.key === flag.key
                          }
                        />
                      </>
                    ),
                  }))}
                  autoHeight={{ twoColumn: true }}
                ></Table>
              )}
            </Section>
          </PageWrapper>
        )
      )}
    </div>
  );
};

export default FeatureFlagsScreen;
